import { useSyncExternalStore } from 'react';
import type { Lookup, Reading } from '@realmery/realm-core';
// The desktop's own thesaurus file, and the word forms it writes for the web (schema/thesaurus/README.md).
import wordNetUrl from '../../../../src/main/resources/thesaurus/oewn-2025.tsv.gz?url';
import wordFormsUrl from '../../../../schema/thesaurus/word-forms-en.tsv.gz?url';
import type { SynonymMenu, ThesaurusRequest, ThesaurusUrls } from './thesaurus.worker.ts';

/**
 * The page's side of the thesaurus (docs/PARITY-STEP-6-PLAN.md section 5): its worker is
 * started the first time anything asks, and `useThesaurusStatus` says whether it's still
 * loading (about 6 MB, the first time), ready, or couldn't be loaded.
 */

export type ThesaurusStatus = 'idle' | 'loading' | 'ready' | 'failed';
export type { SynonymMenu };

const URLS: ThesaurusUrls = { wordNet: wordNetUrl, wordForms: wordFormsUrl };

let worker: Worker | null = null;
let next = 1;
const waiting = new Map<number, { resolve: (value: unknown) => void; reject: (e: Error) => void }>();
let status: ThesaurusStatus = 'idle';
const listeners = new Set<() => void>();

function setStatus(next: ThesaurusStatus) {
  if (status === next) return;
  status = next;
  for (const l of listeners) l();
}

/** A request without its id and the data's addresses, which `ask` adds. */
type Ask = ThesaurusRequest extends infer R ? (R extends ThesaurusRequest ? Omit<R, 'id' | 'urls'> : never) : never;

function ask<T>(request: Ask): Promise<T> {
  if (!worker) {
    worker = new Worker(new URL('./thesaurus.worker.ts', import.meta.url), { type: 'module', name: 'thesaurus' });
    worker.onmessage = (event: MessageEvent<{ id: number; result?: unknown; error?: string }>) => {
      const { id, result, error } = event.data;
      const pending = waiting.get(id);
      waiting.delete(id);
      if (error !== undefined) {
        setStatus('failed');
        pending?.reject(new Error(error));
      } else {
        setStatus('ready');
        pending?.resolve(result);
      }
    };
  }
  if (status !== 'ready') setStatus('loading');
  const id = next++;
  return new Promise<T>((resolve, reject) => {
    waiting.set(id, { resolve: resolve as (value: unknown) => void, reject });
    worker!.postMessage({ ...request, id, urls: URLS } as ThesaurusRequest);
  });
}

export const thesaurus = {
  lookUp: (written: string, before: string | null) => ask<Lookup>({ method: 'lookUp', written, before }),
  menu: (written: string, before: string | null) => ask<SynonymMenu>({ method: 'menu', written, before }),
  fit: (words: readonly string[], reading: Reading | null, original: string) => ask<string[]>({ method: 'fit', words, reading, original }),
};

export function useThesaurusStatus(): ThesaurusStatus {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => status,
  );
}

// ---- the panel's target: the word it was opened on, and how to put another in its place ----

export interface ThesaurusTarget {
  readonly word: string;
  readonly before: string | null;
  /** Puts `word` in place of the target; false if the text there has changed. */
  readonly replace: (word: string) => boolean;
}

/** The Thesaurus panel's state: closed, or open on a word (or on nothing, to browse). */
let panel: { readonly open: boolean; readonly target: ThesaurusTarget | null; readonly opened: number } = { open: false, target: null, opened: 0 };
const panelListeners = new Set<() => void>();

export function openThesaurus(target: ThesaurusTarget | null): void {
  panel = { open: true, target, opened: panel.opened + 1 };
  for (const l of panelListeners) l();
}

export function closeThesaurus(): void {
  panel = { ...panel, open: false };
  for (const l of panelListeners) l();
}

export function useThesaurusPanel() {
  return useSyncExternalStore(
    (l) => {
      panelListeners.add(l);
      return () => panelListeners.delete(l);
    },
    () => panel,
  );
}
